import React, { useCallback, useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { generatePath, useNavigate } from 'react-router';
import styled from 'styled-components';
import { Field, Formik, FormikErrors } from 'formik';
import { DataCard } from '@sdir/sds';
import { FormInput, FormSelect } from '@sdir/blueprint.aps/lib/components';
import { useApi } from '@sdir/httpclient/lib/hooks/useApi';
import { UpsertApplicationGroupRequest } from '@sdir/httpclient/lib/clients/aps/endorsement';
import { ApplicationLayout } from '../Organisms/ApplicationLayout';
import ApplicationSteps from '../Molecules/ApplicationSteps';
import { routes } from '../Routes';
import { useReportees } from '../../services/hooks/useReportees';
import { useCurrentApplication } from '../../services/hooks/useCurrentApplication';
import { endorsementApi } from '../../httpclient';
import { isEmailInvalid } from '../../helpers/validationHelpers';

type ApplicantInput = {
  onBehalf?: string | null;
  name?: string;
  email?: string;
};

const ApplicantPage: React.FC = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const { application: currentApplication, setApplication } = useCurrentApplication();
  const {
    data: { result: reportees },
    callApi: getReportees,
  } = useReportees();
  const requiredText = intl.formatMessage({ id: 'general.form.error' });
  const emailText = intl.formatMessage({ id: 'general.form.error.email' });

  const {
    errorObject: submitError,
    loading: submitLoading,
    callApi: submitStep,
  } = useApi({
    apiFunction: (payload) => endorsementApi.upsertApplicationGroup(payload),
    callApiOnInit: false,
    successCallback: (result) => {
      if (result && setApplication) {
        setApplication({ result });
      }
      const path = generatePath(routes.sailor, { applicationUid: result.uid });
      navigate(path);
    },
  });

  const behalfOptions = useMemo(() => {
    return (
      reportees?.map((r) => ({
        name: r.name ? r.name : r.id,
        value: r.id,
        secondValue: r.name ? r.id : undefined,
      })) || []
    );
  }, [reportees]);

  const onChangeBehalf = useCallback(
    (value: any, setValues: any) => {
      const item = reportees?.find((o) => o.id === value);
      setValues({
        onBehalf: value,
        name: item?.name,
        email: item?.email,
      });
      return value;
    },
    [reportees]
  );

  const handleSubmit = useCallback(
    (values: ApplicantInput) => {
      const existingApplication = currentApplication?.result || {};
      const body: UpsertApplicationGroupRequest = {
        ...existingApplication,
        reporteeId: values?.onBehalf,
        reporteeName: values?.name,
        contactEmail: values?.email,
      };
      submitStep(body);
    },
    [currentApplication?.result, navigate]
  );

  const handleValidation = useCallback(
    (values: ApplicantInput) => {
      const errors: FormikErrors<any> = {};
      if (!values.onBehalf) errors.onBehalf = requiredText;
      if (!values.email) {
        errors.email = requiredText;
      } else if (isEmailInvalid(values.email)) {
        errors.email = emailText;
      }
      return Object.keys(errors).length ? errors : undefined;
    },
    [requiredText, emailText]
  );

  useEffect(() => {
    getReportees();
  }, []);

  const initialValues: ApplicantInput = useMemo(() => {
    const hasSingleReportee = reportees?.length === 1;
    let onBehalf = currentApplication?.result?.reporteeId || null;
    if (!onBehalf && hasSingleReportee) {
      onBehalf = reportees[0].id || null;
    }
    const reportee = onBehalf ? reportees?.find((r) => r.id === onBehalf) : undefined;
    return {
      onBehalf,
      name: currentApplication?.result?.reporteeName || reportee?.name || '',
      email:
        currentApplication?.result?.reporteeEmail ||
        currentApplication?.result?.contactEmail ||
        reportee?.email ||
        '',
    };
  }, [currentApplication?.result, reportees]);

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validate={handleValidation}
      enableReinitialize
    >
      {({ setValues, submitForm, values }) => (
        <form>
          <ApplicationLayout
            canNavNext={!submitLoading}
            canNavBack={!submitLoading}
            heading={intl.formatMessage({ id: 'application.new.title' })}
            ingress={intl.formatMessage({ id: 'application.new.ingress' })}
            exitLabel={intl.formatMessage({ id: 'general.backtoapplications' })}
            navBackLabel={intl.formatMessage({ id: 'general.back' })}
            navNextLabel={intl.formatMessage({ id: 'general.next' })}
            timeline={<ApplicationSteps />}
            onNavNextClick={submitForm}
          >
            <DataCard>
              <ApplicantInfo>
                <ApplicantFields>
                  <h2>{intl.formatMessage({ id: 'application.applicant.title' })}</h2>
                  <FieldRow>
                    <Field
                      name="onBehalf"
                      label={intl.formatMessage({ id: 'application.applicant.onbehalf' })}
                      component={FormSelect}
                      options={behalfOptions}
                      onChange={(v) => onChangeBehalf(v, setValues)}
                      disabled={reportees?.length === 1}
                    />
                    {values?.onBehalf && (
                      <Field
                        name="name"
                        label={intl.formatMessage({ id: 'general.form.name' })}
                        component={FormInput}
                        disabled={!!values?.name}
                      />
                    )}
                  </FieldRow>
                  {values?.onBehalf && (
                    <FieldRow>
                      <div />
                      <Field
                        name="email"
                        label={intl.formatMessage({ id: 'application.applicant.email' })}
                        component={FormInput}
                      />
                    </FieldRow>
                  )}
                </ApplicantFields>
              </ApplicantInfo>
            </DataCard>
          </ApplicationLayout>
        </form>
      )}
    </Formik>
  );
};

export default ApplicantPage;

const ApplicantInfo = styled.div`
  padding: 10px 35px 40px 35px;
  min-height: 400px;
`;

const ApplicantFields = styled.div``;

const FieldRow = styled.div`
  display: flex;
  flex-direction: row;
  margin-bottom: 20px;

  & > * {
    width: 50%;
    &:not(last-child) {
      margin-right: 30px;
    }
  }
`;
